import type { CopilotStatefulToolCall, CopilotToolCallStatus } from '@customerio/hydra-copilot';
import { isToolCallFailed } from '@customerio/hydra-copilot';
import { isEmptyString } from '@/utils/string';
import { extractFilename } from '../helpers/file-utils';
import { capabilityStatuses } from './capability-status';
import { humanizeScope } from './scope-display';

// Exported so the step's icon can follow the same call the "Denied: …" label makes: a
// denial isn't a failure (isToolCallFailed sees no error), so it must not draw a check.
export function isImpactApprovalDenied(toolCall: CopilotStatefulToolCall): boolean {
  return matchCapabilityStatus(toolCall, (status) => status === 'rejected' || status === 'denied');
}

// The action never ran, so the step has to say so — a neutral recovered-failure label
// would imply it did (TAG-1886).
function isApprovalUnanswered(toolCall: CopilotStatefulToolCall): boolean {
  return capabilityStatuses(toolCall.response).some((status) => status === 'unanswered');
}

function isImpactApprovalApproved(toolCall: CopilotStatefulToolCall): boolean {
  return matchCapabilityStatus(toolCall, (status) => status === 'approved' || status === 'partial');
}

function matchCapabilityStatus(toolCall: CopilotStatefulToolCall, pred: (status: string) => boolean): boolean {
  if (toolCall.approval_request == null) return false;
  return capabilityStatuses(toolCall.response).some(pred);
}

function isAwaitingApproval(toolCall: CopilotStatefulToolCall): boolean {
  if (toolCall.needs_approval !== true || toolCall.approval_request == null) return false;
  const response = toolCall.response;
  if (response == null) return toolCall.status === 'executing';
  if (typeof response.error === 'string' && response.error.length > 0) return false;
  return response.result == null && toolCall.status === 'executing';
}

export type ToolCallLabelSet = {
  planned: string;
  executing: string;
  success: string;
  error: string;
};

/**
 * Humanize a snake_case tool name: "search_docs" → "Search docs"
 */
function humanizeToolName(name: string): string {
  const label = name.replaceAll('_', ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// Renders the first scope (+ count) so the step reads "Approved: Edit campaign #42".
function summarizeCapabilityScopes(toolCall: CopilotStatefulToolCall): string | null {
  if (toolCall.name !== 'request_capability') return null;
  const scopes = toolCall.approval_request?.scopes;
  if (scopes == null || scopes.length === 0) {
    const raw = toolCall.args?.scopes;
    if (Array.isArray(raw) && raw.every((s) => typeof s === 'string')) {
      return humanizeScopeList(raw as string[]);
    }
    return null;
  }
  return humanizeScopeList(scopes);
}

function humanizeScopeList(scopes: string[]): string | null {
  if (scopes.length === 0) return null;
  const first = humanizeScope(scopes[0]);
  if (first == null) return null;
  return scopes.length > 1 ? `${first} (+${scopes.length - 1} more)` : first;
}

/**
 * Derive human-readable labels for a tool call.
 * Uses backend summary when available, otherwise generates from tool name/args.
 */
export function getToolCallLabels(toolCall: CopilotStatefulToolCall): ToolCallLabelSet {
  const { name, summary } = toolCall;

  // Always derive from the humanized name so no tool names are hardcoded here.
  // Summaries don't carry error descriptions, so this must be computed independently.
  const error = `Couldn't ${humanizeToolName(name).toLowerCase()}`;

  // Use backend summary when present (e.g. LLM-generated bash summaries)
  if (
    summary != null &&
    !isEmptyString(summary.planned) &&
    !isEmptyString(summary.executing) &&
    !isEmptyString(summary.success)
  ) {
    const planned = summary.planned.trim().replace(/\.+$/, '');
    return {
      planned,
      executing: summary.executing.trim().replace(/\.+$/, '') + '…',
      success: summary.success.trim().replace(/\.+$/, ''),
      // "Couldn't update the email template body content" beats "Couldn't
      // bash" — reuse the summary's intent, lowercasing only the first
      // character so product names inside it keep their casing.
      error: `Couldn't ${planned.charAt(0).toLowerCase()}${planned.slice(1)}`,
    };
  }

  // Tool-specific fallbacks
  if (name === 'search_docs') {
    const query = typeof toolCall.args?.query === 'string' ? toolCall.args.query : '';
    const suffix = query.length > 0 ? ` for "${query}"` : '';
    return {
      planned: `Search docs${suffix}`,
      executing: `Searching docs${suffix}…`,
      success: `Searched docs${suffix}`,
      error,
    };
  }

  if (name === 'read') {
    return { planned: 'Read', executing: 'Reading', success: 'Read', error };
  }

  if (name === 'write') {
    return { planned: 'Write', executing: 'Writing', success: 'Wrote', error };
  }

  if (name === 'bash') {
    // planned is present-tense intent (it also composes into "Waiting for approval: …"
    // and labels a settled-but-failed step) — "Working on it" would claim the step is
    // still running there.
    return { planned: 'Run a command', executing: 'Working on it…', success: 'Done', error };
  }

  if (name === 'request_capability') {
    // Bare scope so the composed label is "Approved: Edit campaign #42", not double-prefixed.
    const scopeLabel = summarizeCapabilityScopes(toolCall);
    if (scopeLabel != null) {
      return {
        planned: scopeLabel,
        executing: scopeLabel,
        success: scopeLabel,
        error: `Couldn't request permission for ${scopeLabel}`,
      };
    }
    return {
      planned: 'Request permission',
      executing: 'Requesting permission…',
      success: 'Permission approved',
      error: "Couldn't request permission",
    };
  }

  // Generic fallback
  const label = humanizeToolName(name);
  return { planned: label, executing: label + '…', success: label, error };
}

/**
 * Get the label for the tool call's current status.
 */
export function getToolCallLabel(toolCall: CopilotStatefulToolCall): string {
  const labels = getToolCallLabels(toolCall);
  if (isImpactApprovalDenied(toolCall)) return `Denied: ${labels.planned}`;
  if (isApprovalUnanswered(toolCall)) return `Not answered: ${labels.planned}`;
  if (toolCall.status === 'error') return labels.error ?? labels.planned;
  // Both "Updated the email template" and "Couldn't update…" are false when the command
  // failed once and the retry worked, so fall back to intent (TAG-1886). Outranks
  // "Approved: …" for the same reason the error label used to.
  if (isToolCallFailed(toolCall)) return labels.planned;
  if (isImpactApprovalApproved(toolCall)) return `Approved: ${labels.planned}`;
  // Approval-paused tcs are still 'executing' with needs_approval=true — override the generic spinner copy.
  if (isAwaitingApproval(toolCall)) return `Waiting for approval: ${labels.planned}`;
  if (toolCall.status === 'executing') return labels.executing;
  if (toolCall.status === 'success') return labels.success;
  return labels.planned;
}

/** Pick the right label string based on execution status. */
function labelForStatus(status: CopilotToolCallStatus, executing: string, success: string, planned: string): string {
  if (status === 'executing') return executing;
  if (status === 'success') return success;
  return planned;
}

/** Derive a filename-bearing header label for file-operation tools (read/write). */
function fileOpHeaderLabel(
  status: CopilotToolCallStatus,
  args: Record<string, unknown> | undefined,
  verbs: [string, string, string],
): string {
  const rawPath = typeof args?.path === 'string' ? args.path : '';
  const filename = rawPath.length > 0 ? extractFilename(rawPath) : 'file';
  return labelForStatus(status, `${verbs[1]} ${filename}`, `${verbs[2]} ${filename}`, `${verbs[0]} ${filename}`);
}

/**
 * Short label for the chain-of-thought header (no query strings, etc.).
 */
export function getToolCallHeaderLabel(toolCall: CopilotStatefulToolCall): string {
  const { name, summary, status } = toolCall;

  // Workflow surfaces denials as errored tool_responses; checking here keeps "Denied: …" over "Couldn't …".
  if (isImpactApprovalDenied(toolCall)) {
    const planned =
      summary != null && !isEmptyString(summary.planned)
        ? summary.planned.trim().replace(/\.+$/, '')
        : getToolCallLabels(toolCall).planned;
    return `Denied: ${planned}`;
  }
  if (isApprovalUnanswered(toolCall)) {
    return `Not answered: ${getToolCallLabels(toolCall).planned}`;
  }

  // The error label reuses the summary's planned intent ("Couldn't update …")
  // when a backend summary is present, falling back to the humanized tool name.
  if (status === 'error') {
    return getToolCallLabels(toolCall).error;
  }

  // Settled but failed — neutral intent, not the success claim (TAG-1886).
  if (isToolCallFailed(toolCall)) {
    return getToolCallLabels(toolCall).planned;
  }

  if (isImpactApprovalApproved(toolCall)) {
    const planned =
      summary != null && !isEmptyString(summary.planned)
        ? summary.planned.trim().replace(/\.+$/, '')
        : getToolCallLabels(toolCall).planned;
    return `Approved: ${planned}`;
  }

  // Override before the backend summary so "Waiting for approval" wins.
  if (isAwaitingApproval(toolCall)) {
    const planned =
      summary != null && !isEmptyString(summary.planned)
        ? summary.planned.trim().replace(/\.+$/, '')
        : getToolCallLabels(toolCall).planned;
    return `Waiting for approval: ${planned}`;
  }

  // Backend summary — use it when all fields are present
  if (
    summary != null &&
    !isEmptyString(summary.planned) &&
    !isEmptyString(summary.executing) &&
    !isEmptyString(summary.success)
  ) {
    return labelForStatus(
      status,
      summary.executing.trim().replace(/\.+$/, '') + '…',
      summary.success.trim().replace(/\.+$/, ''),
      summary.planned.trim().replace(/\.+$/, ''),
    );
  }

  if (name === 'search_docs') return labelForStatus(status, 'Searching docs…', 'Searched docs', 'Search docs');
  if (name === 'read') return fileOpHeaderLabel(status, toolCall.args, ['Read', 'Reading', 'Read']);
  if (name === 'write') return fileOpHeaderLabel(status, toolCall.args, ['Write', 'Writing', 'Wrote']);

  return getToolCallLabel(toolCall);
}
